import React, { useEffect } from 'react';
import * as d3 from 'd3';
import fetch from "node-fetch";

const NetworkDiagram = ({ dataResidue, svgRef, annotation }) => {

  const residueDta = dataResidue
    .filter(r => r[annotation] && String(r[annotation]).trim() !== "-")
    .map(r => ({ data: String(r[annotation]).trim(), anPosition: parseInt(r.anPosition.split("_")[1], 10) }));

  const binding_region = [...new Set(residueDta.map(r => r.data))];

  const nodes = [];
  const links = [];

  async function fetchGeneInfo(accession) {
    const url = `https://rest.uniprot.org/uniprotkb/search?query=accession:${accession}&fields=length,gene_names`;
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    const data = await res.json();
    if (data.results && data.results.length > 0) {
      const entry = data.results[0];
      const length = entry.sequence ? entry.sequence.length : 0;

      let symbol = accession;
      if (entry.genes && entry.genes.length > 0 && entry.genes[0].geneName) {
        symbol = entry.genes[0].geneName.value;
      }
      return { length, symbol };
    }
    return { length: 0, symbol: accession };
  }

  async function getGeneInfoFromMatches(binding_region) {
    const accessions = new Set();
    binding_region.forEach(str => {
      [...str.matchAll(/([A-Z][A-Z0-9]{5}|[OPQ][0-9][A-Z0-9]{3}[0-9])\((\d+)-(\d+)\)/g)]
        .forEach(m => accessions.add(m[1]));
    });

    const lengths = {};
    const symbols = {};

    await Promise.all([...accessions].map(async (acc) => {
      const info = await fetchGeneInfo(acc);
      lengths[acc] = info.length;
      symbols[acc] = info.symbol;
    }));

    return { lengths, symbols };
  }

  useEffect(() => {
    async function buildNetwork() {
      const { lengths: geneLengths, symbols: geneSymbols } = await getGeneInfoFromMatches(binding_region);

      binding_region.forEach(str => {
        const matches = [...str.matchAll(/([A-Z][A-Z0-9]{5}|[OPQ][0-9][A-Z0-9]{3}[0-9])\((\d+)-(\d+)\)/g)];

        matches.forEach((m, index) => {
          const accession = m[1];
          const start = parseInt(m[2], 10);
          const end = parseInt(m[3], 10);
          const symbol = geneSymbols[accession] || accession;

          const existingNode = nodes.find(n => n.id === accession);

          if (!existingNode) {
            nodes.push({
              id: accession,
              label: symbol,
              name: `${symbol} (${accession}) ${m[2]}-${m[3]}`,
              length: geneLengths[accession] || 0,
              domains: [{
                name: "binding region",
                start: start,
                end: end,
                color: "ff006e"
              }]
            });
          } else {

            const alreadyExists = existingNode.domains.some(
              d => d.start === start && d.end === end
            );
            if (!alreadyExists) {
              existingNode.domains.push({
                name: "binding region",
                start: start,
                end: end,
                color: "ff006e"
              });
            }
          }

          if (index > 0) {
            const prevAccession = matches[index - 1][1];
            links.push({
              source: prevAccession,
              target: accession,
              interaction: "gene"
            });
          }
        });
      });

      const data = { nodes, links };

      const svg = d3.select(svgRef.current)
        .attr("version", "1.1")
        .attr("xmlns", "http://www.w3.org/2000/svg")
        .attr("xmlns:xlink", "http://www.w3.org/1999/xlink")
        .attr("id", "network");
      const width = 300;
      const height = 300;
      const padding = 20;
      const rectWidth = 200;
      const rectHeight = 20;
      var reDrawRef = false;

      function dragStarted(event, d) {
        d3.select(this).raise().classed('active', true);
      }

      function dragged(event, d) {
        d.x = event.x;
        d.y = event.y;
        d.fx = d.x;
        d.fy = d.y;
        calculateLinkPositions();
        drawNetwork();
      }

      function dragEnded(event, d) {
        d3.select(this).classed('active', false);
        d.fx = null;
        d.fy = null;
      }

      const drag = d3.drag()
        .on('start', dragStarted)
        .on('drag', dragged)
        .on('end', dragEnded);

      function initializePositions() {
        data.nodes.forEach((node) => {
          node.x = width / 2 + (Math.random() - 0.5) * padding;
          node.y = height / 2 + (Math.random() - 0.5) * padding;
          node.shape = 'circle';
        });
      }

      function calculateForces() {
        const k = 1;
        data.nodes.forEach((node) => {
          node.fx = 0;
          node.fy = 0;

          data.nodes.forEach((otherNode) => {
            if (node !== otherNode) {
              const dx = otherNode.x - node.x;
              const dy = otherNode.y - node.y;
              const distance = Math.sqrt(dx * dx + dy * dy);

              const repulsiveForce = k / distance;
              node.fx -= repulsiveForce * dx;
              node.fy -= repulsiveForce * dy;
            }
          });
        });
      }

      function updatePositions() {
        const alpha = 0.1;
        data.nodes.forEach((node) => {
          node.x += alpha * node.fx;
          node.y += alpha * node.fy;

          node.x = Math.max(padding, Math.min(width - padding, node.x));
          node.y = Math.max(padding, Math.min(height - padding, node.y));
        });
      }

      function calculateLinkPositions() {
        data.links.forEach((link) => {
          const sourceNode = data.nodes.find((node) => node.id === link.source);
          const targetNode = data.nodes.find((node) => node.id === link.target);

          if (sourceNode && targetNode) {
            if (link.interaction === 'binding_region') {
              link.x1 = sourceNode.x + (link.linkPos1 * rectWidth / sourceNode.length);
              link.y1 = sourceNode.y;
              link.x2 = targetNode.x + (link.linkPos2 * rectWidth / targetNode.length);
              link.y2 = targetNode.y;
            } else {
              link.x1 = sourceNode.x;
              link.y1 = sourceNode.y;
              link.x2 = targetNode.x;
              link.y2 = targetNode.y;
            }
          }
        });
      }

      function drawNetwork() {
        svg.selectAll('*').remove();

        svg.selectAll('line')
          .data(data.links)
          .enter()
          .filter(d => d.interaction === "gene")
          .append('line')
          .attr("class", "links")
          .attr('x1', (d) => d.x1)
          .attr('y1', (d) => d.y1)
          .attr('x2', (d) => d.x2)
          .attr('y2', (d) => d.y2)
          .attr("cursor", "crosshair")
          .style("stroke-width", 1.3)
          .attr('stroke', (d) => {
            const colorScale = d3.scaleOrdinal(d3.schemePastel2).domain(["t", "biding_region", "gene"]);
            return colorScale(d.interaction);
          });

        svg.selectAll('g.node')
          .data(data.nodes)
          .enter()
          .append('g')
          .attr('class', 'node')
          .attr('transform', (d) => `translate(${d.x},${d.y})`)
          .attr("cursor", "crosshair")
          .each(function (d) {
            const nodeGroup = d3.select(this);

            nodeGroup.append('title').text(d.name);

            if (d.shape === 'circle') {
              nodeGroup.append('circle')
                .attr('r', Math.log(d.length * 100))
                .attr('fill', '#ffffff')
                .attr('stroke', '#000000')
                .attr('stroke-width', 2);

              if (d.domains) {
                const pie = d3.pie().value((domain) => domain.end - domain.start);
                const arc = d3.arc().innerRadius(0).outerRadius(Math.log(d.length * 100));

                nodeGroup.selectAll('path')
                  .data(pie(d.domains))
                  .enter()
                  .append('g')
                  .append('path')
                  .attr('d', arc)
                  .attr('fill', (domain) => `#${domain.data.color}`)
                  .attr('stroke', (domain) => `#${domain.data.color}`)
                  .append('title')
                  .text((domain) => `${domain.data.name}: ${domain.data.start}-${domain.data.end}`);
              }
            } else if (d.shape === 'rectangle') {
              nodeGroup.append('rect')
                .attr('x', -rectWidth / 2)
                .attr('y', -rectHeight / 2)
                .attr('width', rectWidth)
                .attr('height', rectHeight)
                .attr('fill', '#ffffff')
                .attr('stroke', '#000000');

              if (d.domains) {
                nodeGroup.append('title')
                  .text(() => {
                    return d.domains.map(domain => `${domain.name}: ${domain.start}-${domain.end}`).join('\n');
                  });
              }

              if (d.domains) {
                d.domains.forEach((domain) => {
                  const domainStart = (domain.start * rectWidth) / d.length;
                  const domainEnd = ((domain.end) * rectWidth + 30) / d.length;
                  const domainColor = `#${domain.color}`;

                  nodeGroup.append('rect')
                    .attr('x', -rectWidth / 2 + domainStart)
                    .attr('y', -rectHeight / 2 + 1.40)
                    .attr('width', domainEnd - domainStart)
                    .attr('height', rectHeight - 3)
                    .attr('fill', domainColor)
                    .attr('stroke', domainColor)
                    .append('title')
                    .text(`${domain.name}: ${domain.start}-${domain.end}`);
                });
              }

              nodeGroup.append('text')
                .attr('x', -rectWidth / 2 - 5)
                .attr('y', 0)
                .attr('dy', '0.35em')
                .attr('text-anchor', 'end')
                .attr('fill', 'black')
                .style("text-shadow", "-1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 1px 1px 0 white")
                .text(d.label);

              const lengthLabelsY = rectHeight / 2 + 15;
              const fractionPositions = [0, 0.25, 0.5, 0.75, 1];

              fractionPositions.forEach((position) => {
                const labelText = position === 0 ? '1' : Math.round(position * d.length).toString();
                const labelX = -rectWidth / 2 + position * rectWidth;

                nodeGroup.append('text')
                  .attr('x', labelX)
                  .attr('y', lengthLabelsY)
                  .attr('dy', '0.35em')
                  .attr('text-anchor', 'middle')
                  .attr('fill', 'black')
                  .text(labelText)
                  .style("text-shadow", "-1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 1px 1px 0 white")
                  .style('font-size', '0.75rem');
              });
            }

            if (d.shape === 'circle') {
              nodeGroup.append('text')
                .attr('dy', -Math.log(d.length * 100) - 5)
                .attr('text-anchor', 'middle')
                .attr('fill', 'black')
                .text(d.label);
            }
          }).call(drag);

        svg.selectAll('line')
          .data(data.links)
          .enter()
          .filter(d => d.interaction !== "gene")
          .append('line')
          .attr("class", "links")
          .attr('x1', (d) => d.x1)
          .attr('y1', (d) => d.y1)
          .attr('x2', (d) => d.x2)
          .attr('y2', (d) => d.y2)
          .attr("cursor", "crosshair")
          .style("stroke-width", 1.3)
          .attr('stroke', (d) => {
            const colorScale = d3.scaleOrdinal(d3.schemePastel2).domain(["t", "biding_region", "gene"]);
            return colorScale(d.interaction);
          }).style('visibility', (d) => {
            if (reDrawRef) {
              if ((d.interaction === 'binding_region') && d.visible !== undefined) {
                return d.visible ? 'visible' : 'hidden';
              } else {
                return 'visible';
              }
            } else {
              return 'hidden';
            }
          });
      }

      function handleMouseClick(event, d) {
        event.preventDefault();
        const [mouseX, mouseY] = d3.pointer(event);

        data.nodes.forEach((node) => {
          const distance = Math.sqrt((mouseX - node.x) ** 2 + (mouseY - node.y) ** 2);

          if (
            (node.shape === 'circle' && distance <= Math.log(node.length * 100)) ||
            (node.shape === 'rectangle' &&
              mouseX >= node.x - rectWidth / 2 &&
              mouseX <= node.x + rectWidth / 2 &&
              mouseY >= node.y - rectHeight / 2 &&
              mouseY <= node.y + rectHeight / 2)
          ) {
            node.shape = node.shape === 'circle' ? 'rectangle' : 'circle';

            data.links.forEach((link) => {
              if ((link.interaction === 'binding_region') && (node.id === link.source || node.id === link.target)) {
                const sourceNode = data.nodes.find((n) => n.id === link.source);
                const targetNode = data.nodes.find((n) => n.id === link.target);
                if (
                  sourceNode &&
                  targetNode &&
                  sourceNode.shape === 'rectangle' &&
                  targetNode.shape === 'rectangle'
                ) {
                  reDrawRef = true;
                  link.visible = true;
                } else {
                  link.visible = false;
                }
              }
            });
          }
        });

        calculateLinkPositions();
        drawNetwork();
      }

      initializePositions();

      for (let i = 0; i < 200; i++) {
        calculateForces();
        updatePositions();
      }

      calculateLinkPositions();
      drawNetwork();

      svg.on('dblclick', handleMouseClick);

      return () => {
        svg.on('dblclick', null);
      };
    }

    buildNetwork();
  }, [dataResidue, svgRef, annotation, binding_region]);

  return <svg ref={svgRef} width="100%" height="100%" viewBox={`0 0 600 600`} preserveAspectRatio="xMidYMid meet"></svg>;
};

export default NetworkDiagram;
